import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, UserRound, MapPin, ArrowRight, Loader2, X, Bell, BellOff } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const MaestroRow = ({ m, onRemove, onMute }) => (
  <Card className="p-4 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`fav-maestro-${m.business_id}`}>
    <div className="w-14 h-14 rounded-xl overflow-hidden bg-muted grid place-items-center shrink-0">
      {m.avatar ? <img src={fileUrl(m.avatar)} alt={m.name} className="w-full h-full object-cover" /> : <UserRound className="w-6 h-6 text-muted-foreground" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-bold line-clamp-2 break-words" data-testid={`fav-maestro-name-${m.business_id}`}>{m.name}</p>
      {m.profession && <p className="text-sm text-primary truncate">{m.profession}</p>}
      <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3">
        {m.commune && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{m.commune}</span>}
        <span data-testid={`fav-maestro-count-${m.business_id}`}>{m.workshops_count ? `${m.workshops_count} taller${m.workshops_count === 1 ? "" : "es"} publicado${m.workshops_count === 1 ? "" : "s"}` : "Sin talleres publicados por ahora"}</span>
      </p>
    </div>
    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
      <Button asChild size="sm" className="gap-1" data-testid={`fav-maestro-view-${m.business_id}`}>
        <Link to={`/maestro/${m.business_id}`}>Ver talleres<ArrowRight className="w-3.5 h-3.5" /></Link>
      </Button>
      <button onClick={() => onMute(m)} className={`text-xs inline-flex items-center gap-1 ${m.muted ? "text-amber-700 font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid={`fav-maestro-mute-${m.business_id}`} aria-pressed={m.muted}>
        {m.muted ? <><BellOff className="w-3.5 h-3.5" />Silenciado · Activar avisos</> : <><Bell className="w-3.5 h-3.5" />Silenciar avisos</>}
      </button>
      <button onClick={() => onRemove(m)} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid={`fav-maestro-remove-${m.business_id}`}>
        <X className="w-3.5 h-3.5" />Dejar de seguir
      </button>
    </div>
  </Card>
);

export const FavoriteMaestrosList = () => {
  const [items, setItems] = useState(null);
  const load = () => api.get("/favorite-maestros").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const mute = async (m) => {
    try {
      await api.patch(`/favorite-maestros/${m.business_id}/mute`, { muted: !m.muted });
      toast.success(m.muted ? `Volverás a recibir avisos de ${m.name}` : `Silenciaste a ${m.name}: lo sigues, pero sin avisos`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (m) => {
    try { await api.delete(`/favorite-maestros/${m.business_id}`); toast.success(`Dejaste de seguir a ${m.name}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (items === null) return <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (items.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground" data-testid="fav-maestros-empty">
        <Star className="w-10 h-10 mx-auto mb-3 opacity-40" />
        Aún no sigues a ningún Maestro. Busca un Maestro por su nombre o entra a su perfil y pulsa “Seguir”.
        <div><Button asChild variant="link"><Link to="/explorar">Explorar talleres</Link></Button></div>
      </div>
    );
  }
  return <div className="space-y-3" data-testid="fav-maestros-list">{items.map((m) => <MaestroRow key={m.business_id} m={m} onRemove={remove} onMute={mute} />)}</div>;
};
